import { useState, type FormEvent, type JSX } from 'react';
import { useNavigate } from 'react-router';
import { authApi } from '../api/client.js';
import { isApiError } from '../api/errors.js';
import { useAuth } from '../auth/context.js';
import { useBrand } from '../brand/context.js';
import { useLegal } from '../brand/legal-context.js';
import { useI18n } from '../i18n/context.js';

/** Необратимое удаление остаётся за повторным вводом пароля. */
export function DeleteAccount(): JSX.Element {
  const { t } = useI18n();
  /* The period the brand publishes, so this screen and the privacy policy cannot disagree. */
  const { quarantineTerm } = useLegal();
  const { support } = useBrand();
  const { setUser } = useAuth();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [pending, setPending] = useState(false);

  const submit = (event: FormEvent): void => {
    event.preventDefault();
    setError(null);
    setPending(true);
    void authApi
      .deleteAccount(password)
      .then(() => {
        setUser(null);
        void navigate('/', { replace: true });
      })
      .catch((cause: unknown) => {
        setError(
          isApiError(cause) && cause.status === 403
            ? t.account.wrongPassword
            : t.common.unexpectedError,
        );
        setPending(false);
      });
  };

  return (
    <section
      className="kitup-card grid gap-4 border-red-300 p-5"
      aria-labelledby="delete-account-title"
    >
      <div>
        <h2 id="delete-account-title" className="font-semibold">
          {t.account.title}
        </h2>
        <p className="mt-1 max-w-prose text-sm text-[var(--kitup-color-muted)]">
          {t.account.warning(quarantineTerm)}
        </p>
        {/* "Write to us" said where: the legal wording above stays as the policy has it. */}
        <p className="mt-1 text-sm text-[var(--kitup-color-muted)]">
          {t.account.writeToUs}{' '}
          <a className="underline" href={`mailto:${support.email}`}>
            {support.email}
          </a>
        </p>
      </div>

      {!open ? (
        <div>
          <button
            type="button"
            className="kitup-secondary-button justify-self-start border-red-300 text-red-700"
            onClick={() => setOpen(true)}
          >
            {t.account.delete}
          </button>
        </div>
      ) : (
        <form onSubmit={submit} className="grid gap-4">
          <label className="grid max-w-sm gap-1.5 text-sm font-medium">
            {t.account.password}
            <input
              type="password"
              autoComplete="current-password"
              value={password}
              onChange={(event) => setPassword(event.target.value)}
              className="kitup-input"
              required
            />
          </label>

          {error ? (
            <p role="alert" className="text-sm text-red-700">
              {error}
            </p>
          ) : null}

          <div className="flex flex-wrap gap-3">
            <button className="kitup-primary-button" disabled={pending || !password}>
              {t.account.confirm}
            </button>
            <button
              type="button"
              className="kitup-secondary-button"
              onClick={() => {
                setOpen(false);
                setPassword('');
                setError(null);
              }}
            >
              {t.account.cancel}
            </button>
          </div>
        </form>
      )}
    </section>
  );
}
